import React, { useState } from 'react';
import { upsertCompat } from '../../api/governance';
import { ApiError } from '../../api/request';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const PLACEHOLDER = `[
  { "deviceKey": "xtool-p2s", "xcode": "MID-1001", "processType": "cut",     "isCompatible": true },
  { "deviceKey": "xtool-p2s", "xcode": "MID-1002", "processType": "engrave", "isCompatible": false, "reason": "材料过厚, 烟雾过大" }
]`;

function CompatBatchUploadModal({ open, onClose, onSaved }: Props) {
    const [raw, setRaw] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!open) return null;

    const reset = () => {
        setRaw('');
        setError('');
    };

    const handleClose = () => {
        if (busy) return;
        reset();
        onClose();
    };

    const submit = async () => {
        setError('');
        if (!raw.trim()) {
            setError('请粘贴 JSON 内容');
            return;
        }
        let arr: unknown;
        try {
            arr = JSON.parse(raw);
        } catch (e) {
            setError('JSON 解析失败: ' + String(e));
            return;
        }
        if (!Array.isArray(arr) || arr.length === 0) {
            setError('内容必须是非空数组');
            return;
        }
        const items: Array<{ deviceKey: string; xcode: string; processType?: string; isCompatible: boolean; reason?: string }> = [];
        for (let i = 0; i < arr.length; i++) {
            const it = arr[i] as Record<string, unknown>;
            if (!it || typeof it !== 'object') {
                setError(`第 ${i + 1} 条不是对象`);
                return;
            }
            const dk = typeof it.deviceKey === 'string' ? it.deviceKey.trim() : '';
            const mid = typeof it.xcode === 'string' ? it.xcode.trim() : '';
            // 兼容多种 JSON 写法: true/false, 1/0, "true"/"false"
            let compat: boolean;
            if (typeof it.isCompatible === 'boolean') {
                compat = it.isCompatible;
            } else if (it.isCompatible === 1 || it.isCompatible === '1' || it.isCompatible === 'true') {
                compat = true;
            } else if (it.isCompatible === 0 || it.isCompatible === '0' || it.isCompatible === 'false') {
                compat = false;
            } else {
                setError(`第 ${i + 1} 条 isCompatible 必须是 boolean / 0 / 1`);
                return;
            }
            if (!dk || !mid) {
                setError(`第 ${i + 1} 条缺少 deviceKey / xcode`);
                return;
            }
            items.push({
                deviceKey: dk,
                xcode: mid,
                processType: typeof it.processType === 'string' ? it.processType.trim() : '',
                isCompatible: compat,
                reason: typeof it.reason === 'string' ? it.reason : '',
            });
        }

        setBusy(true);
        try {
            await upsertCompat(items);
            reset();
            onSaved();
            onClose();
        } catch (e) {
            const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError('提交失败: ' + msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 !m-0 z-40 flex items-start justify-center bg-slate-900/40 pt-20">
            <div className="card w-[680px] max-w-[95vw] p-5 space-y-4 bg-white">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-base font-semibold">批量上传 设备 × 材料 适配</div>
                        <div className="text-xs text-slate-500">
                            粘贴 JSON 数组, 后端按 (deviceKey, xcode, processType) 三元组 upsert
                        </div>
                    </div>
                    <button type="button" className="btn-ghost" onClick={handleClose} disabled={busy}>
                        关闭
                    </button>
                </div>

                <div>
                    <label className="block text-xs text-slate-500 mb-1">JSON 内容</label>
                    <textarea
                        className="inp font-mono text-xs"
                        rows={12}
                        placeholder={PLACEHOLDER}
                        value={raw}
                        onChange={(e) => setRaw(e.target.value)}
                        disabled={busy}
                    />
                    <div className="text-[11px] text-slate-400 mt-1">
                        字段: <code>deviceKey</code>, <code>xcode</code>, <code>processType</code> (可空 =
                        全部工序), <code>isCompatible</code> (true/false), <code>reason</code> (不兼容时建议填)
                    </div>
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-ghost" onClick={handleClose} disabled={busy}>
                        取消
                    </button>
                    <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
                        {busy ? '提交中…' : '提交'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CompatBatchUploadModal;
